import { useLiveQuery } from 'dexie-react-hooks';
import type { Job, JobStatus } from '@/entities/job';
import { getDB } from '@/shared/storage';

export interface JobNode {
  job: Job;
  children: JobNode[];
}

export function useJobsTree(): JobNode[] {
  const all = useLiveQuery<Job[]>(
    () => getDB().jobs.orderBy('createdAt').reverse().toArray(),
    [],
  );
  return buildTree(all ?? []);
}

export function useJob(jobId: string | undefined): Job | undefined {
  return useLiveQuery(() => (jobId ? getDB().jobs.get(jobId) : undefined), [jobId]);
}

export function useJobChildren(jobId: string | undefined): Job[] | undefined {
  return useLiveQuery(
    () => (jobId ? getDB().jobs.where('parentJobId').equals(jobId).toArray() : []),
    [jobId],
  );
}

export function useUnfinishedCount(): number {
  const count = useLiveQuery(
    () =>
      getDB()
        .jobs.where('status')
        .anyOf(['running', 'interrupted', 'failed'])
        .count(),
    [],
  );
  return count ?? 0;
}

function buildTree(jobs: readonly Job[]): JobNode[] {
  const byParent = new Map<string, Job[]>();
  for (const j of jobs) {
    if (!j.parentJobId) continue;
    const arr = byParent.get(j.parentJobId) ?? [];
    arr.push(j);
    byParent.set(j.parentJobId, arr);
  }
  for (const arr of byParent.values()) arr.sort((a, b) => a.createdAt - b.createdAt);

  const make = (job: Job): JobNode => ({
    job,
    children: (byParent.get(job.id) ?? []).map(make),
  });

  return jobs.filter((j) => !j.parentJobId).map(make);
}

const ACTIVE_STATUSES: readonly JobStatus[] = ['running', 'pending'];

export function partitionRootsByActivity(roots: readonly JobNode[]): {
  active: JobNode[];
  history: JobNode[];
} {
  const active: JobNode[] = [];
  const history: JobNode[] = [];
  for (const n of roots) {
    if (ACTIVE_STATUSES.includes(n.job.status)) active.push(n);
    else history.push(n);
  }
  return { active, history };
}

export function aggregateRootCounts(roots: readonly JobNode[]): Record<JobStatus, number> {
  const acc: Record<JobStatus, number> = {
    pending: 0,
    running: 0,
    succeeded: 0,
    failed: 0,
    canceled: 0,
    interrupted: 0,
  };
  for (const n of roots) acc[n.job.status] += 1;
  return acc;
}
